import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import MessageBubble from "@/components/MessageBubble";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import AppBadge from "@/components/AppBadge";
import NotificationBell from "@/components/NotificationBell";
import HelpAssistant from "@/components/HelpAssistant";
import { syncPush } from "@/lib/push";
import {
  LayoutDashboard, Users, CalendarDays, ClipboardCheck, TrendingUp,
  CreditCard, Info, LogOut, Menu, X, Trophy, Tags, Dumbbell, Contact, Megaphone, ShieldCheck, ScrollText, Shirt, UserCog, Award, Mail, HeartHandshake, MapPin, Calculator, HandHelping, FileImage,
} from "lucide-react";

const ROLE_LABELS = { admin: "Administrateur", coach: "Coach", player: "Joueur", parent: "Parent" };

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard },

  { to: "/actualites", label: "Actualités", icon: Megaphone, group: "Vie du club" },
  { to: "/planning", label: "Planning", icon: CalendarDays, group: "Vie du club" },
  { to: "/lieux", label: "Lieux", icon: MapPin, roles: ["admin", "coach"], group: "Vie du club" },
  { to: "/annuaire", label: "Organigramme", icon: Contact, group: "Vie du club" },
  { to: "/coachs", label: "Nos Coachs", icon: Award, group: "Vie du club" },
  { to: "/notre-comite", label: "Le Comité", icon: HeartHandshake, group: "Vie du club" },
  { to: "/categories", label: "Catégories", icon: Tags, cap: "nav_categories", group: "Vie du club" },
  { to: "/club", label: "Infos Club", icon: Info, group: "Vie du club" },
  { to: "/sponsors", label: "Nos partenaires", icon: HeartHandshake, group: "Vie du club" },

  { to: "/membres", label: "Affiliés", icon: Users, group: "Suivi sportif" },
  { to: "/competences", label: "Compétences", icon: TrendingUp, group: "Suivi sportif" },
  { to: "/presence", label: "Présence", icon: ClipboardCheck, cap: "nav_presence", altRoles: ["parent", "player"], group: "Suivi sportif" },
  { to: "/entrainements", label: "Entraînements", icon: Dumbbell, cap: "nav_entrainements", altRoles: ["admin", "coach"], group: "Suivi sportif" },
  { to: "/cotisations", label: "Financier", icon: CreditCard, cap: "nav_cotisations", group: "Suivi sportif" },
  { to: "/pack", label: "Pack vêtements", icon: Shirt, pack: true, group: "Suivi sportif" },

  { to: "/comite", label: "Gestion du comité", icon: HeartHandshake, committee: true, group: "Gestion du comité" },
  { to: "/benevoles", label: "Bénévoles", icon: HandHelping, volunteer: true, group: "Gestion du comité" },

  { to: "/utilisateurs", label: "Utilisateurs", icon: UserCog, roles: ["admin"], group: "Administration" },
  { to: "/emails", label: "Emails à envoyer", icon: Mail, roles: ["admin"], group: "Administration" },
  { to: "/droits", label: "Droits d'accès", icon: ShieldCheck, roles: ["admin"], group: "Administration" },
  { to: "/audit", label: "Journal d'audit", icon: ScrollText, roles: ["admin"], group: "Administration" },
  { to: "/infographies", label: "Infographies & PDF", icon: FileImage, roles: ["admin"], group: "Administration" },
  { to: "/comptabilite", label: "Comptabilité", icon: Calculator, treasurer: true, group: "Administration" },
];

const GROUP_ORDER = ["Vie du club", "Suivi sportif", "Gestion du comité", "Administration"];

export default function Layout({ children }) {
  const { user, logout, can, packManage, committee, treasurer, volunteer, hasRole } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => { syncPush(); }, []);

  const items = NAV.filter((n) => (!n.roles || hasRole(...n.roles)) && (!n.cap || can(n.cap) || (n.altRoles && n.altRoles.some((r) => hasRole(r)))) && (!n.pack || packManage) && (!n.committee || committee) && (!n.treasurer || treasurer) && (!n.volunteer || volunteer));
  const ungrouped = items.filter((n) => !n.group);
  const groups = GROUP_ORDER
    .map((g) => ({ name: g, items: items.filter((n) => n.group === g) }))
    .filter((g) => g.items.length);

  const groupLabel = (g) => {
    if (g === "Suivi sportif") {
      if (hasRole("admin")) return "Suivi sportif";
      if (hasRole("coach")) return "Mes séances & joueurs";
      if (hasRole("parent")) return "Mon enfant";
    }
    return g;
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  const NavItem = (n) => (
    <NavLink
      key={n.to}
      to={n.to}
      end={n.to === "/"}
      onClick={() => setOpen(false)}
      data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
          isActive
            ? "bg-primary/15 text-primary border-l-2 border-primary"
            : "text-muted-foreground hover:text-foreground hover:bg-secondary"
        }`
      }
    >
      <n.icon size={18} />
      {n.label}
    </NavLink>
  );

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 h-16 bg-card border-b border-border">
        <div className="flex items-center gap-2">
          <Trophy className="text-primary" size={22} />
          <span className="font-display font-extrabold uppercase tracking-tight">FKA Quaregnon</span>
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell variant="mobile" />
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="text-foreground p-1">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`fixed md:static z-30 top-0 left-0 h-full w-64 bg-card border-r border-border flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}>
        <div className="hidden md:flex items-center gap-2 px-6 h-20 border-b border-border">
          <Trophy className="text-primary" size={26} />
          <span className="font-display text-xl font-extrabold uppercase tracking-tighter">FKA Quaregnon</span>
          <div className="ml-auto"><NotificationBell variant="desktop" /></div>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto mt-16 md:mt-0">
          {ungrouped.map((n) => NavItem(n))}
          {groups.map((g) => (
            <div key={g.name} className="pt-4">
              <p className="px-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">{groupLabel(g.name)}</p>
              <div className="space-y-1">{g.items.map((n) => NavItem(n))}</div>
            </div>
          ))}
        </nav>
        <div className="border-t border-border p-4">
          <div className="mb-3">
            <p className="text-sm font-bold truncate">{user?.name}</p>
            <p className="text-xs uppercase tracking-widest text-primary">{ROLE_LABELS[user?.role]}</p>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-4 py-2.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
          >
            <LogOut size={16} /> Déconnexion
          </button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/60 z-20 md:hidden" onClick={() => setOpen(false)} />}

      <main className="flex-1 min-w-0 pt-16 md:pt-0 overflow-x-hidden">
        <div className="p-5 md:p-8 max-w-7xl">{children}</div>
      </main>

      <MessageBubble />
      <PWAInstallPrompt />
      <AppBadge />
      <HelpAssistant />
    </div>
  );
}
